import CloseButton from '@components/button/CloseButton'
import { useDialog } from '@globals/dialog/useDialogStore'
import theme, { colors } from '@globals/themes/defaultTheme'
import { spacing, tabletQuery } from '@globals/themes/themeConstants'
import { MediaObjectUrl } from '@kudos/http-client'
import Box from '@mui/material/Box'
import Dialog, { DialogProps } from '@mui/material/Dialog'
import Drawer from '@mui/material/Drawer'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { SxProps } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import UserAvatar from '@shared/avatar/UserAvatar'
import { t } from '@transifex/native'
import { ReactNode } from 'react'
import { DialogContentStyled } from './Dialog.styled'

const paperStyle: SxProps = {
  overflowX: 'hidden',
  background: colors.blueCardGradient,
}

const headerStyle: SxProps = {
  width: '100%',
  alignItems: 'center',
  pr: 6,
}

const titleChipStyle: SxProps = {
  width: '25%',
  flex: '1 1 33%',
  verticalAlign: 'middle',
  alignItems: 'center',
}

const titleStyle: SxProps = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: colors.white,
  flex: '1 2 auto',
  pr: 1,
  mt: 0,
  [tabletQuery]: {
    mt: 0,
  },
}

const closeButtonStyle: SxProps = {
  background: 'transparent',
  top: spacing(1),
  right: spacing(1),
  '&.MuiIconButton-root': {
    backgroundColor: 'transparent !important',
  },
  '& .MuiSvgIcon-root': {
    color: `${colors.white} !important`,
  },
}

type EngagementDialogWithMobileDrawerProps = {
  dialogKey: string
  children: React.ReactNode
  onClose?: () => void
  title?: React.ReactNode
  subtitle?: ReactNode
  avatarUrls?: MediaObjectUrl | null
  defaultIcon?: ReactNode
  chip?: ReactNode
  isFormDirty?: boolean
}

const EngagementDialogWithMobileDrawer: React.FC<EngagementDialogWithMobileDrawerProps> = ({
  dialogKey,
  children,
  onClose,
  title,
  subtitle,
  avatarUrls,
  defaultIcon,
  chip,
  isFormDirty,
}) => {
  const isTablet = useMediaQuery(tabletQuery)
  const { closeDialog, isOpen } = useDialog(dialogKey)

  const handleOnClose: DialogProps['onClose'] = (event, reason) => {
    if (!!reason && reason === 'backdropClick' && isFormDirty) return
    if (!!onClose) return onClose()
    closeDialog()
  }

  return isTablet ? (
    <Drawer
      anchor="bottom"
      variant="temporary"
      open={isOpen}
      PaperProps={{
        sx: paperStyle,
      }}
      onClose={handleOnClose}
      sx={{ zIndex: theme.zIndex.drawer + 1 }}>
      <Stack direction="row" spacing={1.5} p={3} sx={headerStyle} alignItems="flex-start">
        {(!!avatarUrls || !!defaultIcon) && (
          <UserAvatar media={avatarUrls} size={50} defaultIcon={defaultIcon} />
        )}
        <Stack direction="row" sx={titleChipStyle}>
          <Stack sx={titleStyle}>
            <Typography variant="h2" component="h2" fontWeight={600} sx={titleStyle}>
              {title}
            </Typography>
            <Typography variant="caption" color={colors.white} sx={titleStyle}>
              {subtitle}
            </Typography>
            <Box sx={{ mt: 0.5 }}>{chip}</Box>
          </Stack>
        </Stack>
        <CloseButton
          id="btnCloseSendAwardDrawer"
          ariaLabel={t('Close send award dialog')}
          onClose={closeDialog}
          sx={closeButtonStyle}
        />
      </Stack>
      <Box px={2} pt={title ? 0 : 6} pb={2} sx={{ backgroundColor: colors.white }}>
        {children}
      </Box>
    </Drawer>
  ) : (
    <Dialog
      aria-label={t('Custom dialog')}
      open={isOpen}
      scroll="paper"
      fullWidth
      PaperProps={{
        sx: paperStyle,
      }}
      disableScrollLock={isOpen}
      id={`${dialogKey}-dialog`}
      onClose={handleOnClose}>
      <Stack direction="row" spacing={1.5} p={3} sx={headerStyle}>
        {(!!avatarUrls || !!defaultIcon) && (
          <UserAvatar media={avatarUrls} size={50} defaultIcon={defaultIcon} />
        )}
        <Stack direction="row" sx={titleChipStyle}>
          <Stack sx={titleStyle}>
            <Typography variant="h2" component="h2" fontWeight={600} sx={titleStyle}>
              {title}
            </Typography>
            <Typography variant="caption" color={colors.white} sx={titleStyle}>
              {subtitle}
            </Typography>
          </Stack>
          {chip}
          <CloseButton
            id="btnCloseSendAwardDialog"
            ariaLabel={t('Close send award dialog')}
            onClose={closeDialog}
            sx={closeButtonStyle}
          />
        </Stack>
      </Stack>
      <DialogContentStyled sx={{ backgroundColor: colors.white }}>{children}</DialogContentStyled>
    </Dialog>
  )
}

export default EngagementDialogWithMobileDrawer
